import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { useNavigate } from 'react-router-dom';
import { MessageSquare, Star, Send, Heart } from 'lucide-react';
import api from '../utils/api';
import { toast } from 'sonner';
import { useLanguage } from '../contexts/LanguageContext';
import { DictationButton } from '../components/DictationButton';

const RatingScale = ({ value, onChange, label, description, language }) => {
  return (
    <div className="mb-6">
      <label className="block font-sans text-[#F8F5F2] mb-2">{label}</label>
      {description && (
        <p className="font-sans text-sm text-[#A1A1AA] mb-3">{description}</p>
      )}
      <div className="flex gap-2">
        {[1, 2, 3, 4, 5].map((num) => (
          <button
            key={num}
            type="button"
            onClick={() => onChange(num)}
            className={`w-12 h-12 rounded-xl font-sans font-semibold transition-all duration-300 ${
              value === num
                ? 'bg-[#D4AF37] text-[#030305] scale-110'
                : 'bg-white/5 text-[#A1A1AA] hover:bg-white/10 hover:text-[#F8F5F2]'
            }`}
            data-testid={`rating-${num}`}
          >
            {num}
          </button>
        ))}
      </div>
      <div className="flex justify-between mt-1">
        <span className="text-xs text-[#6B7280]">{language === 'tr' ? 'Hiç' : 'Not at all'}</span>
        <span className="text-xs text-[#6B7280]">{language === 'tr' ? 'Çok' : 'Very much'}</span>
      </div>
    </div>
  );
};

export const FeedbackSurvey = ({ isModal = false, onComplete }) => {
  const navigate = useNavigate();
  const { language } = useLanguage();
  const [submitting, setSubmitting] = useState(false);
  const [currentStep, setCurrentStep] = useState(0);
  
  const [formData, setFormData] = useState({
    questions_made_sense: 0,
    sun_map_accurate: 0,
    reading_personal: 0,
    action_useful: 0,
    would_use_weekly: 0,
    what_confused: '',
    what_would_add: '',
    would_pay_10: '',
    other_comments: ''
  });

  const questions = [
    {
      type: 'rating',
      key: 'questions_made_sense',
      label: language === 'tr' ? 'Sorular mantıklı mıydı?' : 'Did the questions make sense?',
      description: language === 'tr' ? 'Değerlendirme soruları anlaşılır mıydı?' : 'Were the assessment questions clear and easy to understand?'
    },
    {
      type: 'rating',
      key: 'sun_map_accurate',
      label: language === 'tr' ? 'Güneş Haritanız doğru hissettirdi mi?' : 'Did your Sun Map feel accurate?',
      description: language === 'tr' ? 'Radar grafiği hayatınızı ne kadar iyi yansıttı?' : 'How well did the radar chart reflect your life?'
    },
    {
      type: 'rating',
      key: 'reading_personal',
      label: language === 'tr' ? 'AI okuması kişisel mi yoksa jenerik mi hissettirdi?' : 'Did the AI reading feel personal or generic?',
      description: language === 'tr' ? '1 = Çok jenerik, 5 = Çok kişisel' : '1 = Very generic, 5 = Very personal'
    },
    {
      type: 'rating',
      key: 'action_useful',
      label: language === 'tr' ? 'Önerilen eylem faydalı mıydı?' : 'Was the suggested action useful?',
      description: language === 'tr' ? 'Öncelikler ve alışkanlıklar işe yarar mıydı?' : 'Were the priorities and habits actionable?'
    },
    {
      type: 'rating',
      key: 'would_use_weekly',
      label: language === 'tr' ? 'Bunu haftalık kullanır mıydınız?' : 'Would you use this weekly?',
      description: language === 'tr' ? 'Bu uygulamaya düzenli olarak geri döner misiniz?' : 'Would you come back to this app regularly?'
    },
    {
      type: 'text',
      key: 'what_confused',
      label: language === 'tr' ? 'Sizi ne şaşırttı veya karıştırdı?' : 'What confused you?',
      placeholder: language === 'tr' ? 'Herhangi bir kafa karıştırıcı kısım...' : 'Any confusing parts...'
    },
    {
      type: 'text',
      key: 'what_would_add',
      label: language === 'tr' ? 'Ne eklerdiniz?' : 'What would you add?',
      placeholder: language === 'tr' ? 'Özellik önerileri...' : 'Feature suggestions...'
    },
    {
      type: 'choice',
      key: 'would_pay_10',
      label: language === 'tr' ? 'Bu ayda $10 olsa, kullanmayı düşünür müydünüz?' : 'If this cost $10/month, would you consider using it?',
      options: [
        { value: 'yes', label: language === 'tr' ? 'Evet' : 'Yes' },
        { value: 'maybe', label: language === 'tr' ? 'Belki' : 'Maybe' },
        { value: 'no', label: language === 'tr' ? 'Hayır' : 'No' }
      ]
    },
    {
      type: 'text',
      key: 'other_comments',
      label: language === 'tr' ? 'Başka yorum veya öneri?' : 'Any other comments or suggestions?',
      placeholder: language === 'tr' ? 'Düşüncelerinizi paylaşın...' : 'Share your thoughts...'
    }
  ];

  const handleSubmit = async () => {
    // Validate required fields
    if (formData.questions_made_sense === 0 || 
        formData.sun_map_accurate === 0 || 
        formData.reading_personal === 0 ||
        formData.action_useful === 0 ||
        formData.would_use_weekly === 0 ||
        !formData.would_pay_10) {
      toast.error(language === 'tr' ? 'Lütfen tüm derecelendirmeleri tamamlayın' : 'Please complete all ratings');
      return;
    }

    setSubmitting(true);
    try {
      await api.post('/feedback/submit', formData);
      toast.success(language === 'tr' ? 'Geri bildiriminiz için teşekkürler!' : 'Thank you for your feedback!');
      
      if (onComplete) {
        onComplete();
      } else {
        navigate('/dashboard');
      }
    } catch (error) {
      toast.error(language === 'tr' ? 'Geri bildirim gönderilemedi' : 'Failed to submit feedback');
    } finally {
      setSubmitting(false);
    }
  };

  const currentQuestion = questions[currentStep];
  const isLastStep = currentStep === questions.length - 1;
  const canProceed = () => {
    const q = questions[currentStep];
    if (q.type === 'rating') return formData[q.key] > 0;
    if (q.type === 'choice') return formData[q.key] !== '';
    return true; // text fields are optional
  };

  const content = (
    <div className="max-w-2xl mx-auto">
      {/* Header */}
      <div className="text-center mb-8">
        <div className="w-16 h-16 rounded-full bg-[#D4AF37]/20 flex items-center justify-center mx-auto mb-4">
          <Heart className="w-8 h-8 text-[#D4AF37]" />
        </div>
        <h1 className="font-serif font-light text-3xl text-[#F8F5F2] mb-3">
          {language === 'tr' ? 'IN\'i Denediğiniz İçin Teşekkürler' : 'Thanks for trying IN'}
        </h1>
        <p className="font-sans text-[#A1A1AA] mb-4">
          {language === 'tr' 
            ? 'Geliştirmeye çalışıyorum, bu yüzden dürüst geri bildirim gerçekten yardımcı oluyor.'
            : 'I\'m trying to improve it, so honest feedback really helps.'}
        </p>
        {/* Skip button - centered in header */}
        {isModal && (
          <button
            onClick={() => onComplete && onComplete()}
            className="text-[#A1A1AA] hover:text-[#D4AF37] text-sm font-sans transition-colors underline"
            data-testid="skip-survey-button"
          >
            {language === 'tr' ? 'Şimdilik geç' : 'Skip for now'}
          </button>
        )}
      </div>

      {/* Progress */}
      <div className="mb-8">
        <div className="flex justify-between text-xs text-[#A1A1AA] mb-2">
          <span>{language === 'tr' ? 'Soru' : 'Question'} {currentStep + 1} / {questions.length}</span>
          <span>{Math.round(((currentStep + 1) / questions.length) * 100)}%</span>
        </div>
        <div className="h-1 bg-white/10 rounded-full overflow-hidden">
          <motion.div
            className="h-full bg-[#D4AF37]"
            initial={{ width: 0 }}
            animate={{ width: `${((currentStep + 1) / questions.length) * 100}%` }}
          />
        </div>
      </div>

      {/* Question */}
      <motion.div
        key={currentStep}
        initial={{ opacity: 0, x: 20 }}
        animate={{ opacity: 1, x: 0 }}
        exit={{ opacity: 0, x: -20 }}
        className="glass-panel rounded-2xl p-8 mb-6"
      >
        {currentQuestion.type === 'rating' && (
          <RatingScale
            value={formData[currentQuestion.key]}
            onChange={(val) => setFormData({ ...formData, [currentQuestion.key]: val })}
            label={currentQuestion.label}
            description={currentQuestion.description}
            language={language}
          />
        )}

        {currentQuestion.type === 'text' && (
          <div>
            <label className="block font-sans text-[#F8F5F2] mb-3">{currentQuestion.label}</label>
            <div className="relative">
              <textarea
                value={formData[currentQuestion.key]}
                onChange={(e) => setFormData({ ...formData, [currentQuestion.key]: e.target.value })}
                placeholder={currentQuestion.placeholder}
                rows={4}
                className="w-full bg-black/30 border border-white/10 rounded-xl px-4 py-3 pr-12 text-[#F8F5F2] font-sans placeholder:text-[#6B7280] focus:outline-none focus:border-[#D4AF37]/50 resize-none"
                data-testid={`input-${currentQuestion.key}`}
              />
              <div className="absolute top-3 right-3">
                <DictationButton onTranscript={(text) => setFormData(prev => ({ ...prev, [currentQuestion.key]: prev[currentQuestion.key] ? prev[currentQuestion.key] + ' ' + text : text }))} />
              </div>
            </div>
            <p className="text-xs text-[#6B7280] mt-2">
              {language === 'tr' ? '(İsteğe bağlı)' : '(Optional)'}
            </p>
          </div>
        )}

        {currentQuestion.type === 'choice' && (
          <div>
            <label className="block font-sans text-[#F8F5F2] mb-4">{currentQuestion.label}</label>
            <div className="flex flex-col sm:flex-row gap-3">
              {currentQuestion.options.map((option) => (
                <button
                  key={option.value}
                  type="button"
                  onClick={() => setFormData({ ...formData, [currentQuestion.key]: option.value })}
                  className={`flex-1 py-4 px-6 rounded-xl font-sans font-medium transition-all duration-300 ${
                    formData[currentQuestion.key] === option.value
                      ? 'bg-[#D4AF37] text-[#030305]'
                      : 'bg-white/5 text-[#A1A1AA] hover:bg-white/10 hover:text-[#F8F5F2]'
                  }`}
                  data-testid={`choice-${option.value}`}
                >
                  {option.label}
                </button>
              ))}
            </div>
          </div>
        )}
      </motion.div>

      {/* Navigation */}
      <div className="flex justify-between gap-4">
        <button
          onClick={() => setCurrentStep(Math.max(0, currentStep - 1))}
          disabled={currentStep === 0}
          className="px-6 py-3 rounded-xl border border-white/10 text-[#A1A1AA] hover:text-[#F8F5F2] hover:bg-white/5 transition-all disabled:opacity-30 disabled:cursor-not-allowed font-sans"
        >
          {language === 'tr' ? 'Geri' : 'Back'}
        </button>

        {isLastStep ? (
          <button
            onClick={handleSubmit}
            disabled={submitting || !canProceed()}
            className="flex-1 flex items-center justify-center gap-2 px-8 py-3 rounded-xl bg-[#D4AF37] text-[#030305] font-sans font-semibold hover:bg-[#D4AF37]/90 transition-all disabled:opacity-50"
            data-testid="submit-feedback-button"
          >
            {submitting ? (
              <span>{language === 'tr' ? 'Gönderiliyor...' : 'Submitting...'}</span>
            ) : (
              <>
                <Send className="w-4 h-4" />
                {language === 'tr' ? 'Geri Bildirimi Gönder' : 'Submit Feedback'}
              </>
            )}
          </button>
        ) : (
          <button
            onClick={() => setCurrentStep(currentStep + 1)}
            disabled={!canProceed()}
            className="flex-1 px-8 py-3 rounded-xl bg-[#D4AF37] text-[#030305] font-sans font-semibold hover:bg-[#D4AF37]/90 transition-all disabled:opacity-50"
            data-testid="next-question-button"
          >
            {language === 'tr' ? 'Sonraki' : 'Next'}
          </button>
        )}
      </div>
    </div>
  );

  if (isModal) {
    return (
      <div className="fixed inset-0 bg-black/80 backdrop-blur-sm z-50 flex items-center justify-center p-4 overflow-y-auto">
        <div className="w-full max-w-2xl my-8">
          {content}
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen py-8 px-6 relative z-10">
      {content}
    </div>
  );
};

export default FeedbackSurvey;
